import React from "react";
import { VoucherType } from "@/constants";
import { formatDaysValidity, formatDiscount, usePageTitle } from "@/utilities";
import { List, TextInput, useListContext } from "react-admin";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Grid,
  Chip,
} from "@mui/material";
import { appConfig } from "@/commons";
import { Voucher } from "@/types";
import { VoucherTypeSelect } from "@/components/selects";
import { DaysCheckBoxInput } from "@/components/dayscheckboxgroup";

const voucherFilters = [
  <TextInput
    source="q"
    label="Recherche de récompense par nom, référence ..."
    alwaysOn
    key="search"
  />,
  <VoucherTypeSelect source="vt" label="Type de récompense" alwaysOn />,
  <DaysCheckBoxInput source="dv" label="Jour de validité" key="day_validity" />,
];

const VoucherCardGrid: React.FC = () => {
  const { data, isLoading } = useListContext();

  if (isLoading || !data) return null;

  return (
    <Grid container spacing={2} alignItems="stretch">
      {data.map((record: Voucher) => {
        return (
          <Grid
            size={{ xs: 12, sm: 3, md: 4 }}
            key={record.id}
            sx={{ display: "flex" }}
          >
            <Card sx={{ display: "flex", p: 1.5, flex: 1 }}>
              <CardMedia
                component="img"
                sx={{ width: 158 }}
                image={
                  record.img
                    ? appConfig.api.baseUrl + record.img
                    : "/voucher_thumbnail.png"
                }
                alt="Live from space album cover"
              />
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                <CardContent
                  sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                >
                  <Typography variant="subtitle1" component="div">
                    Ref. : {record.reference}{" "}
                    {record.voucherConsumed?.some(
                      (v) => v.type === "consumed",
                    ) ? (
                      <Chip label="Consommé" color="primary" />
                    ) : record.voucherConsumed?.some(
                        (v) => v.type === "claim",
                      ) ? (
                      <Chip label="Réclamé" color="secondary" />
                    ) : null}
                  </Typography>
                  <Typography
                    component="div"
                    variant="h5"
                    sx={{ fontWeight: "bold" }}
                  >
                    {VoucherType.getVoucherTypeLabel(record.voucher_type)}
                  </Typography>
                  <Typography variant="subtitle1" component="div">
                    {record.item}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.disabled"
                    sx={{ fontWeight: "bold" }}
                  >
                    {formatDiscount(
                      record.discount_value,
                      record.discount_type,
                    )}
                  </Typography>
                  <Typography variant="subtitle1" component="div">
                    Qté : {record.quantity}
                  </Typography>
                  {/* Push this block to the bottom */}
                  <Box sx={{ mt: "auto" }}>
                    <Typography variant="body2" color="text.secondary">
                      Jour de validité:{" "}
                      {formatDaysValidity(record.day_validity)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Du {new Date(record.start_at).toLocaleDateString()} au{" "}
                      {new Date(record.end_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const VouchersList: React.FC = () => {
  usePageTitle("Gestion des vouchers");

  return (
    <List
      filters={voucherFilters}
      sort={{ field: "created_at", order: "DESC" }}
      exporter={false}
    >
      <VoucherCardGrid />
    </List>
  );
};

export default VouchersList;
